import type { Estimator } from '../api'
import { pct } from './scale'

/**
 * The poll as a scoreboard: who is on what, and how wide the uncertainty is.
 *
 * ## Why this replaced the interval field on the landing view
 *
 * What used to be here was Figure 1 — every option drawn as its sampling distribution, equal
 * area under each curve, two overprinting inks darkening where the poll cannot separate two
 * candidates. It is the correct instrument and it is beautiful, and an aspirant looking at it
 * could not tell you who was winning.
 *
 * That is the whole problem. These are candidates, not statisticians: the first question is
 * "what is my number and am I in front", and a curve answers it only for a reader who already
 * knows that the height of a density means nothing and the width means everything. The plates
 * are still one click away for whoever wants them — nothing was deleted — but the LANDING
 * view now answers the question that was actually asked.
 *
 * ## The range is drawn, not just printed
 *
 * A single bar invites a candidate to believe the number is exact, which is the error every
 * poll story makes. So each row shows the share as a solid bar and the margin as a lighter
 * band around its end: the eye reads "about here, somewhere in this span". Two candidates
 * whose bands visibly overlap are the case where the next line on the board — the verdict —
 * has to be read before anybody celebrates.
 *
 * ## It computes nothing
 *
 * Every number is the server's: `estimate`, `ciLower`, `ciUpper`. The bars are a rendering of
 * those three, scaled to the widest upper bound so the eye compares like with like.
 */
export function Scoreboard({ data }: { data: Estimator }) {
  const rows = data.bands
    .filter((b) => !b.suppressed && b.estimate !== null)
    .sort((a, b) => (b.estimate ?? 0) - (a.estimate ?? 0))

  if (rows.length === 0) {
    return (
      <p className="px-4 py-6 text-erp-sm text-muted-foreground">
        {data.bands.some((b) => b.suppressed)
          ? 'Every option on this question is held back — too few interviews to publish a '
            + 'figure anybody should act on.'
          : 'No published figures on this question yet.'}
      </p>
    )
  }

  // Scaled to the widest upper bound rather than to 100%, so a six-way race does not render
  // as six short stubs against mostly empty paper.
  const ceiling = Math.max(...rows.map((r) => r.ciUpper ?? r.estimate ?? 0), 0.01)
  const suppressed = data.bands.filter((b) => b.suppressed).length

  return (
    <div className="space-y-3 px-4 py-4">
      {rows.map((row, i) => {
        const share = row.estimate ?? 0
        const lo = row.ciLower ?? share
        const hi = row.ciUpper ?? share
        return (
          <div key={row.optionId} className="grid grid-cols-[minmax(7rem,9rem)_1fr_4.5rem]
                                             items-center gap-3">
            <span className={'truncate text-erp-sm ' + (i === 0 ? 'font-medium' : '')}
                  title={row.optionLabel}>
              {row.optionLabel}
            </span>

            <div className="relative h-5 rounded bg-muted/40">
              {/* The margin, drawn first and underneath: the span the true figure sits in. */}
              <div className="absolute inset-y-0 rounded bg-primary/20"
                   style={{ left: `${(lo / ceiling) * 100}%`,
                            width: `${Math.max(((hi - lo) / ceiling) * 100, 0.5)}%` }}
                   aria-hidden />
              {/* The estimate itself. */}
              <div className={'absolute inset-y-0 left-0 rounded '
                              + (i === 0 ? 'bg-primary' : 'bg-primary/55')}
                   style={{ width: `${(share / ceiling) * 100}%` }}
                   aria-hidden />
            </div>

            <span className="text-right text-erp-sm tabular-nums">
              {pct(share)}
            </span>
          </div>
        )
      })}

      <p className="pt-1 text-erp-xs text-muted-foreground">
        The solid bar is the figure the poll measured. The pale band around its end is the
        range the true figure is almost certainly inside — a poll measures a span, never a
        point.
        {suppressed > 0 && (
          <> {suppressed} option{suppressed === 1 ? ' is' : 's are'} held back for having too
            few interviews to publish.</>
        )}
      </p>
    </div>
  )
}
